import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import labels from './Labels/AddTalentModalLabel';
import CustomAlert from './CustomAlert';

const AjouterTalentModal = ({ open, onClose ,onSuccess  }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [showUpdateConfirm, setShowUpdateConfirm] = useState('');
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [alertMessages, setAlertMessages] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);

  const role = localStorage.getItem('userType');

  const baseRoute =   role === 'admin'
    ? '/admin/add-talent'
    : role === 'commercial'
    ? '/commercial/add-talent'
    : role === 'agent'
    ? '/agent/add-talent'
    : role === 'recruteur'
    ? '/recruteur/add-talent'
    : '';

  const handleManual = () => {
    onClose();
    navigate(baseRoute);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    setError('');
  };

  const handleRemoveFile = (fileName) => {
    setFiles(prevFiles => prevFiles.filter(file => file.name !== fileName));
  };

  const handleExtract = async () => {
    if (files.length === 0) {
      setError(labels.ajouterTalent.errorNoCV);
      return;
    }

    try {
      setLoading(true);
      const allParsedData = [];

      for (const file of files) {
        const reader = new FileReader();
        const fileBase64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const payload = {
          name: file.name,
          type: file.type,
          file: fileBase64,
        };

        const res = await fetch('https://www.cooptaleo.com/jsoncv', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (!data || typeof data !== 'object') {
          setError(labels.ajouterTalent.extractError(file.name));
          return;
        }

        allParsedData.push({
          data,
          fileInfo: {
            name: file.name,
            type: file.type,
            base64: `data:${file.type};base64,${fileBase64}`,
          },
        });
      }

      if (files.length === 1) {
        const first = allParsedData[0];
        const success = true;

        localStorage.setItem('cvParsedData', JSON.stringify(first.data));
        localStorage.setItem('cvFileName', first.fileInfo.name);
        localStorage.setItem('cvFileFromModal', JSON.stringify(first.fileInfo));
    
        if (!success) {
          setShowUpdateConfirm(true);
        }else{
          onSuccess?.(); 
          onClose();
          navigate(`${baseRoute}?parsed=true`);
        }  
      } else {
        let filesToKeep = [];
let anySuccess = false;

for (const parsedItem of allParsedData) {
  const success = true;
  if (!success) {
    filesToKeep.push(parsedItem.fileInfo.name); 
  } else {
    anySuccess = true;
  }
}

setFiles(prev => prev.filter(f => filesToKeep.includes(f.name)));
setExistingFiles(filesToKeep);

if (anySuccess) {
  setAlertMessage("Les nouveaux talents ont été ajoutés.");
  setAlertSeverity("success");
  setAlertOpen(true);
  onSuccess?.();
}

if (filesToKeep.length > 0) {
  setAlertMessage("Certains talents existent déjà.");
  setAlertSeverity("warning");
  setAlertOpen(true);
}
      }
    } catch (err) {
      console.error('Erreur parsing:', err);
      setError(labels.ajouterTalent.apiError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle><b>{labels.ajouterTalent.title}</b></DialogTitle>
      <CustomAlert
        open={alertOpen}
        message={alertMessage}
        severity={alertSeverity}
        onClose={() => setAlertOpen(false)}
      />
      <DialogContent>
        <Typography>{labels.ajouterTalent.instructions}</Typography>
        <Box className="modal-container">
          {files.length > 0 ? (
            <>
              <Typography className="file-name green">{labels.ajouterTalent.cvAdded}</Typography>
              {files.map((f, index) => {
                const hasError = alertMessages.includes(f.name);
                return (
                  <Box key={index} className="file-box">
                    <Typography className={`file-name ${hasError ? 'red' : 'green'}`}>
                      • {f.name}
                    </Typography>
                    <DeleteIcon
                      onClick={() => handleRemoveFile(f.name)}
                      className="hover-delete"
                    />
                  </Box>
                );
              })}
            </>
          ) : (
            <>
              <input type="file" accept=".pdf" id="upload-cv" multiple hidden onChange={handleFileChange} />
              <label htmlFor="upload-cv">
                <Button variant="outlined" component="span" startIcon={<UploadFileIcon />}>
                  {labels.ajouterTalent.uploadButton}
                </Button>
              </label>
            </>
          )}
          {error && (
            <Typography className="error-message">
              {error}
            </Typography>
          )}
        </Box>
        <Typography variant="body2">
          {labels.ajouterTalent.manualFormNote}
        </Typography>
        {alertOpen && (
          <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
            {alertMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleManual} disabled={loading}>
          {labels.ajouterTalent.manualButton}
        </Button>
        <Button onClick={handleExtract} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : labels.ajouterTalent.extractButton}
        </Button>
      </DialogActions>
    </Dialog>
          <Dialog open={showUpdateConfirm} onClose={() => setShowUpdateConfirm(false)}>
          <DialogTitle>CV déjà existant</DialogTitle>
          <DialogContent>
            <Typography>
              Ce talent existe déjà. Voulez-vous mettre à jour ses informations ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowUpdateConfirm(false)} color="error">
              Non
            </Button>
            <Button
              color="primary"
              onClick={() => {
                setShowUpdateConfirm(false);
                onClose();
                navigate(`${baseRoute}?parsed=true`);
              }}
            >
              Oui, mettre à jour
            </Button>
          </DialogActions>
        </Dialog>
</>  
  );
};

export default AjouterTalentModal;
